import { ButtonSmallSubtleIcon, Icon } from "@studio3t/generic-components";
import styled, { useTheme } from "styled-components";
import { JsonViewer } from "@/components/JsonViewer";
import {
  rhaiHighlightStyle,
  rhaiLanguage,
} from "@/pages/NewStudio/rhaiLanguage";

// Rhai language support for the read-only viewer — the Lezer parser's
// tree gives highlighting and folding alike.
const RHAI_LANGUAGE = rhaiLanguage;

import type { Service } from "@/types/api";
import {
  ScriptCard,
  ScriptEditorWrapper,
  ScriptHeader,
  ScriptName,
} from "./styles";

interface ScriptsSectionProps {
  sources: NonNullable<Service["sources"]>;
  onCopy: (text: string) => void;
  /** Asks for the script's deletion — the caller confirms via modal. */
  onDelete: (filename: string) => void;
}

// The script cards alone — their section context (the tab bar) is the
// caller's (Figma 13984-223188).
export function ScriptsSection({
  sources,
  onCopy,
  onDelete,
}: ScriptsSectionProps) {
  const theme = useTheme();
  return (
    <>
      {sources.map((script) => (
        <ScriptCard key={script.filename}>
          <ScriptHeader>
            <ScriptName>{script.filename}</ScriptName>
            <ScriptActions>
              {/* Icon-only copy, as on the workspace detail script cards. */}
              <ButtonSmallSubtleIcon
                icon={Icon.Copy}
                onClick={() => onCopy(script.content)}
                aria-label={`Copy ${script.filename}`}
              />
              {/* Icon-only delete: the red bin, as in the actions menus;
                  the click opens the caller's confirm modal. */}
              <ButtonSmallSubtleIcon
                icon={Icon.Bin}
                // theme.errorIcon carries no `active` color and IconSVG
                // reads it with no fallback — without one the bin paints
                // nothing while pressed.
                colors={{ ...theme.errorIcon, active: theme.errorIcon.default }}
                onClick={() => onDelete(script.filename)}
                aria-label={`Delete ${script.filename}`}
              />
            </ScriptActions>
          </ScriptHeader>
          <ScriptEditorWrapper>
            {/* The shared Raw-configuration CodeMirror template speaking
                Rhai, read-only with the right-click Copy menu — same as the
                workspace detail script cards. */}
            <JsonViewer
              value={script.content}
              language={RHAI_LANGUAGE}
              highlight={rhaiHighlightStyle}
            />
          </ScriptEditorWrapper>
        </ScriptCard>
      ))}
    </>
  );
}

const ScriptActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;
